import { useState } from "react";
import { Download, Share } from "lucide-react";
import { Button } from "@strivelabs/ui-core/v2";
import { startDownload } from "@/components/library/libraryEntry";
import { ShareLinkPopover } from "@/components/share/ShareLinkPopover";
import { APP_ROUTES } from "@/constants";
import type { ShareSurface } from "@/lib/analytics";

/** The app page that shows one artifact to members. */
const artifactAppPath = (artifactId: string) => `${APP_ROUTES.HOME}/artifacts/${encodeURIComponent(artifactId)}`;

interface ArtifactShareActionProps {
  artifactId: string;
  title: string;
  surface: ShareSurface;
  /** The version on screen, which is what a download saves. */
  version?: number;
  /** The same bytes, presigned to arrive as a named file. */
  downloadUrl?: string;
  /**
   * Download by other means than following `downloadUrl` as it is: the
   * artifact page signs a fresh URL first, because a page left open outlives
   * the hour a URL lasts.
   */
  onDownload?: () => Promise<void>;
  /**
   * `xs` for a panel's 52px header strip, beside its other 28px controls;
   * `sm` for a page header.
   */
  size?: "xs" | "sm";
}

/**
 * The one Share control for an artifact, wherever it opens: who can open it
 * (Share) and saving it as a file (Export), together, so a panel header holds
 * one control rather than a share icon beside a download icon.
 *
 * Who may change access is the popover's to decide: a guest gets Export only.
 */
export function ArtifactShareAction({
  artifactId,
  title,
  surface,
  version,
  downloadUrl,
  onDownload,
  size = "xs",
}: ArtifactShareActionProps) {
  if (!artifactId) {
    return null;
  }

  // A labelled control rather than a bare glyph, so it reads as the way out
  // of the app for this page, with a quiet icon beside the word.
  const trigger = (
    <Button variant="secondary" size={size} icon={<Share />}>
      Share
    </Button>
  );

  const canDownload = onDownload !== undefined || Boolean(downloadUrl);

  return (
    <ShareLinkPopover
      entityType="artifact"
      entityId={artifactId}
      surface={surface}
      trigger={trigger}
      title={title}
      privatePath={artifactAppPath(artifactId)}
      exportContent={canDownload ? (
        <ArtifactExport version={version} downloadUrl={downloadUrl} onDownload={onDownload} />
      ) : undefined}
    />
  );
}

interface ArtifactExportProps {
  version?: number;
  downloadUrl?: string;
  onDownload?: () => Promise<void>;
}

/** The Export tab for an artifact: the page as a standalone HTML file. */
function ArtifactExport({ version, downloadUrl, onDownload }: ArtifactExportProps) {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (onDownload) {
      setDownloading(true);
      try {
        await onDownload();
      } finally {
        setDownloading(false);
      }
      return;
    }
    if (downloadUrl) startDownload(downloadUrl);
  };

  return (
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-v2-sm font-medium">HTML file</p>
        <p className="text-v2-xs text-v2-muted-foreground">
          {version === undefined ? "The page" : `Version ${version}`} as one .html file. Images
          won't load in the downloaded file.
        </p>
      </div>
      <Button
        variant="secondary"
        size="sm"
        icon={<Download />}
        onClick={() => void download()}
        state={downloading ? "loading" : undefined}
        loadingText="Preparing…"
        className="shrink-0"
      >
        Download
      </Button>
    </div>
  );
}
